import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStoreState } from 'easy-peasy';
import dayjs from 'dayjs';
import Button from 'Components/nui/Button';
import UserContent from 'Components/nui/UserContent';
import classNames from 'classnames';
import { getStatus } from '~/utils/bannerUtils';
import { IBanner, useDisplayBanners } from '~/hooks/useBanners';

const Banner = () => {
  const solutionId = useStoreState((state: any) => state.auth.solution?.id);
  const { data: displayBanners = [] } = useDisplayBanners();

  const banners = useMemo(
    () =>
      displayBanners.filter((banner) => {
        if (getStatus(banner) !== 'live') return false;
        if (!banner.solution) return true;
        const bannerSolutionId =
          typeof banner.solution === 'string'
            ? banner.solution
            : banner.solution.id;
        if (bannerSolutionId === solutionId) return true;
        return false;
      }),
    [displayBanners, solutionId]
  );

  const [hidden, setHidden] = useState<Record<string, boolean>>({});

  const toggle = (banner: IBanner) => {
    setHidden((state) => ({ ...state, [banner.id]: !state[banner.id] }));
  };

  if (banners.length === 0) return null;

  return (
    <div>
      {banners
        .filter((banner) => !hidden[banner.id])
        .map((banner) => (
          <BannerItem
            key={banner.id}
            banner={banner}
            onClose={() => toggle(banner)}
          />
        ))}
    </div>
  );
};

interface BannerItemProps {
  banner: IBanner;
  onClose: () => void;
}

const BannerItem = ({ banner, onClose }: BannerItemProps) => {
  return (
    <div
      className={classNames('nui-banner', 'nui-alert', 'has-icon', {
        info: banner.style === 'info',
        warning: banner.style === 'warning',
      })}
    >
      {banner.subject && (
        <h4>
          {banner.start && (
            <span className="date">{dayjs(banner.start).format('l')} | </span>
          )}
          {banner.subject}
        </h4>
      )}
      {banner.content && <UserContent data={banner.content} />}

      {banner.action_url && banner.action_text && (
        <>
          {banner.action_url.startsWith('http') ? (
            <a
              href={banner.action_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {banner.action_text}
            </a>
          ) : (
            <Link to={banner.action_url}>{banner.action_text}</Link>
          )}
        </>
      )}
      <Button
        htmlType="button"
        type="reverse"
        className="closeme"
        onClick={onClose}
      >
        &times;
      </Button>
    </div>
  );
};

export default Banner;
